(() => {
  const elementName = 'product-tabs-showcase';
  if (customElements.get(elementName)) return;

  class ProductTabsShowcase extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.galleries = new Map();
      this.breakpoint = window.matchMedia('(max-width: 767px)');
      this.breakpoint.addEventListener('change', () => this.updateGallery(), options);
      window.addEventListener('load', () => this.updateGallery(), options);
      this.parentElement?.querySelector('link[rel="stylesheet"]')?.addEventListener('load', () => this.updateGallery(), options);
      this.updateGallery();

      this.addEventListener('click', event => {
        const tab = event.target.closest('[role="tab"]');
        if (this.tabs.includes(tab)) this.selectTab(tab);
      }, options);

      this.addEventListener('keydown', event => {
        const current = event.target.closest('[role="tab"]');
        const index = this.tabs.indexOf(current);
        if (index < 0) return;
        const last = this.tabs.length - 1;
        const rtl = getComputedStyle(this).direction === 'rtl';
        let next;
        switch (event.key) {
          case 'ArrowRight': next = (index + (rtl ? last : 1)) % this.tabs.length; break;
          case 'ArrowLeft': next = (index + (rtl ? 1 : last)) % this.tabs.length; break;
          case 'Home': next = 0; break;
          case 'End': next = last; break;
          default: return;
        }
        event.preventDefault();
        this.selectTab(this.tabs[next], true);
      }, options);

      this.addEventListener('submit', event => {
        const form = event.target;
        if (!form.matches('[data-prt-cart-form]')) return;
        event.preventDefault();
        this.addToCart(form);
      }, options);

      document.addEventListener('shopify:block:select', event => {
        const panel = this.panels.find(candidate => candidate.dataset.blockId === event.detail?.blockId);
        if (!panel) return;
        const tab = this.tabs.find(candidate => candidate.getAttribute('aria-controls') === panel.id);
        if (tab) this.selectTab(tab);
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.galleries?.forEach(swiper => swiper.destroy(true, true));
      this.galleries?.clear();
      // An in-flight cart request must finish even if the editor removes this section.
      // Aborting a POST does not guarantee Shopify has not already added the item.
    }

    selectTab(selected, focus = false) {
      this.tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach(panel => {
        panel.hidden = panel.id !== selected.getAttribute('aria-controls');
      });
      this.updateGallery(true);
      if (focus) selected.focus({ preventScroll: true });

      // Scroll only the tab strip, so switching never moves the page vertically.
      const strip = this.querySelector('.prt__tabs-scroll');
      const stripBounds = strip.getBoundingClientRect();
      const tabBounds = selected.getBoundingClientRect();
      if (tabBounds.left < stripBounds.left) {
        strip.scrollBy({ left: tabBounds.left - stripBounds.left - 4, behavior: 'auto' });
      } else if (tabBounds.right > stripBounds.right) {
        strip.scrollBy({ left: tabBounds.right - stripBounds.right + 4, behavior: 'auto' });
      }
    }

    updateGallery(reset = false) {
      const panel = this.panels.find(candidate => !candidate.hidden);
      if (this.breakpoint.matches && panel?.classList.contains('prt__panel--hide-mobile-image')) return;
      const gallery = panel?.querySelector('[data-prt-gallery]');
      if (!gallery || !gallery.clientWidth || gallery.dataset.imageCount === '1') return;
      if (typeof window.Swiper !== 'function') return;

      let swiper = this.galleries.get(gallery);
      if (!swiper) {
        const pagination = gallery.querySelector('[data-prt-pagination]');
        swiper = new window.Swiper(gallery, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
          loop: false,
          autoplay: false,
          watchOverflow: true,
          grabCursor: true,
          pagination: {
            el: pagination,
            clickable: true,
            bulletClass: 'prt__dot',
            bulletActiveClass: 'prt__dot--active',
            renderBullet: (index, className) => `<button type="button" class="${className}" aria-label="Show image ${index + 1}"></button>`
          },
          a11y: { enabled: true },
          on: {
            init: instance => this.syncGallery(instance),
            slideChange: instance => this.syncGallery(instance)
          }
        });
        this.galleries.set(gallery, swiper);
      }
      swiper.update();
      if (reset) swiper.slideTo(0, 0);
      this.syncGallery(swiper);
    }

    syncGallery(swiper) {
      swiper.slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== swiper.activeIndex));
      });
      swiper.pagination?.bullets?.forEach((bullet, index) => {
        bullet.setAttribute('aria-current', String(index === swiper.activeIndex));
      });
    }

    async addToCart(form) {
      const button = form.querySelector('button[type="submit"]');
      if (!button || button.disabled || form.dataset.submitting === 'true') return;
      const status = form.closest('[role="tabpanel"]').querySelector('[data-prt-cart-status]');
      const label = button.querySelector('[data-prt-cart-label]');
      const originalLabel = label.textContent;
      const routeRoot = window.Shopify?.routes?.root || '/';
      const body = new FormData(form);
      body.set('quantity', '1');
      form.dataset.submitting = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = 'Adding…';
      status.hidden = true;
      status.textContent = '';

      try {
        const response = await fetch(`${routeRoot}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(typeof result.description === 'string'
            ? result.description
            : 'Unable to add this product. Please try again.');
        }

        window.location.assign(`${routeRoot}checkout`);
      } catch (error) {
        status.textContent = error instanceof TypeError
          ? 'Unable to confirm the cart update. Please check your cart before trying again.'
          : error instanceof SyntaxError
            ? 'Unable to confirm the cart update. Please check your cart before trying again.'
            : error.message || 'Unable to add this product. Please try again.';
        status.hidden = false;
      } finally {
        delete form.dataset.submitting;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        label.textContent = originalLabel;
      }
    }
  }

  customElements.define(elementName, ProductTabsShowcase);
})();
